import { request } from './base.service';

// ── Types ──

export type SuggestionKind =
    | 'create_template'
    | 'duplicate_month'
    | 'duplicate_subset'
    | 'roll_forward'
    | 'adjust_template'
    | 'duplicate_entry'
    | 'create_entry'
    | 'create_entries';

export type CatKey = 'housing' | 'utilities' | 'transport' | 'food' | 'groceries' | 'entertainment' | 'health' | 'income' | 'other';

export interface SuggestionEntry {
    title: string;
    amount: number | null;
    date: string;
    cat: CatKey;
    delta?: string;
}

export interface Suggestion {
    id: string;
    kind: SuggestionKind;
    title: string;
    summary: string;
    primaryLabel: string;
    confidence: number;
    total: number | null;
    reasoning: string;
    entries: SuggestionEntry[];
}

// ── Category palette ──

export const CAT_COLORS: Record<CatKey, { bg: string; fg: string; icon: string }> = {
    housing:       { bg: '#FEF3C7', fg: '#D97706', icon: 'home' },
    utilities:     { bg: '#DBEAFE', fg: '#2563EB', icon: 'bolt' },
    transport:     { bg: '#E0E7FF', fg: '#4F46E5', icon: 'directions_car' },
    food:          { bg: '#D1FAE5', fg: '#059669', icon: 'restaurant' },
    groceries:     { bg: '#D1FAE5', fg: '#059669', icon: 'shopping_cart' },
    entertainment: { bg: '#FCE7F3', fg: '#DB2777', icon: 'movie' },
    health:        { bg: '#FEE2E2', fg: '#DC2626', icon: 'favorite' },
    income:        { bg: '#D1FAE5', fg: '#059669', icon: 'trending_up' },
    other:         { bg: '#F1F5F9', fg: '#475569', icon: 'more_horiz' },
};

export const KIND_ICONS: Record<SuggestionKind, string> = {
    create_template:  'bookmark_add',
    duplicate_month:  'content_copy',
    duplicate_subset: 'library_add',
    roll_forward:     'event_repeat',
    adjust_template:  'tune',
    duplicate_entry:  'copy_all',
    create_entry:     'add',
    create_entries:   'add',
};

// ── Mock data (used until backend is ready) ──

const MOCK_SUGGESTIONS: Suggestion[] = [
    {
        id: 's1',
        kind: 'create_template',
        title: 'Create a "Monthly bills" template',
        summary: "You've added the same 6 bills every month for 3 months. Save them as a template to one-tap next time.",
        primaryLabel: 'Create template',
        confidence: 0.94,
        total: -2147.30,
        reasoning: 'These 6 entries appeared on similar dates in Feb, Mar, and Apr 2026 with stable amounts (\u00b12%). They look like fixed monthly bills.',
        entries: [
            { title: 'Rent',              amount: -1800,   date: '15th',  cat: 'housing' },
            { title: 'Hydro-Qu\u00e9bec', amount: -150,    date: '25th',  cat: 'utilities' },
            { title: 'Vid\u00e9otron Internet', amount: -78.21, date: '30th', cat: 'utilities' },
            { title: 'Car Insurance',     amount: -150,    date: '28th',  cat: 'transport' },
            { title: 'Spotify',           amount: -10.99,  date: '3rd',   cat: 'entertainment' },
            { title: 'Daycare',           amount: -350,    date: '22nd',  cat: 'health' },
        ],
    },
    {
        id: 's2',
        kind: 'duplicate_month',
        title: 'Duplicate April into May',
        summary: '12 of your April entries look recurring. Copy them forward to start May with a head start.',
        primaryLabel: 'Duplicate to May',
        confidence: 0.81,
        total: -2487.50,
        reasoning: '12 of 18 April entries match patterns from March. Variable groceries and fuel are excluded.',
        entries: [
            { title: 'Rent',              amount: -1800,   date: 'May 15', cat: 'housing' },
            { title: 'Daycare',           amount: -350,    date: 'May 22', cat: 'health' },
            { title: 'Hydro-Qu\u00e9bec', amount: -150,    date: 'May 25', cat: 'utilities' },
            { title: 'Car Insurance',     amount: -150,    date: 'May 28', cat: 'transport' },
            { title: '+ 8 more',          amount: null,    date: '',       cat: 'other' },
        ],
    },
    {
        id: 's3',
        kind: 'duplicate_subset',
        title: 'Duplicate groceries to next week',
        summary: "Whole Foods and Metro repeat weekly. Copy this week's 2 entries into May 5\u20139.",
        primaryLabel: 'Duplicate to May 5',
        confidence: 0.72,
        total: -187.40,
        reasoning: 'Grocery entries averaged $93.70/week over the last 6 weeks. The pattern is weekly with low variance.',
        entries: [
            { title: 'Whole Foods', amount: -103.08, date: 'May 6', cat: 'food' },
            { title: 'Metro',       amount: -84.32,  date: 'May 8', cat: 'food' },
        ],
    },
    {
        id: 's4',
        kind: 'roll_forward',
        title: 'Roll forward "Daycare" by 2 days',
        summary: 'Daycare on Apr 22 will push your balance to \u2212$112.70. Move it to Apr 24 (after payday) to stay positive.',
        primaryLabel: 'Move to Apr 24',
        confidence: 0.88,
        total: -350,
        reasoning: 'Your projected balance hits \u2212$112.70 on Apr 22. Income of $1,200 lands Apr 23. Moving Daycare 2 days clears the dip.',
        entries: [
            { title: 'Daycare', amount: -350, date: 'Apr 24', cat: 'health' },
        ],
    },
    {
        id: 's5',
        kind: 'adjust_template',
        title: 'Update "Monthly bills" amounts',
        summary: 'Your last 3 actuals diverged from the template by avg +4.2%. Refresh the template to match reality.',
        primaryLabel: 'Update amounts',
        confidence: 0.66,
        total: null,
        reasoning: 'Hydro-Qu\u00e9bec actuals: $147, $151, $156. Template still says $140. Two other items also drifted >3%.',
        entries: [
            { title: 'Hydro-Qu\u00e9bec', amount: -151.33, date: 'avg', cat: 'utilities', delta: '+8.1%' },
            { title: 'Spotify',            amount: -11.99,  date: 'avg', cat: 'entertainment', delta: '+9.1%' },
            { title: 'Daycare',            amount: -360,    date: 'avg', cat: 'health', delta: '+2.9%' },
        ],
    },
];

export const QUICK_REPLIES = [
    'Any duplicates I can make?',
    'What else can I templatize?',
    'How am I tracking for May?',
    'Show only high-confidence',
];

// ── API calls ──

export const getSuggestions = async (userid: string, month?: string): Promise<Suggestion[]> => {
    try {
        const params = new URLSearchParams({ userid });
        if (month) params.set('month', month);
        const res = await request(`/suggestions?${params.toString()}`);
        return res?.suggestions || res || [];
    } catch {
        // Fallback to mock data while backend is in progress
        return MOCK_SUGGESTIONS;
    }
};

export const applySuggestion = (
    sessionId: string,
    id: string,
    userid: string,
    listid: string,
): Promise<{ ok: boolean; templateId?: string; createdEntries?: any[] }> =>
    request(`/suggestions/sessions/${encodeURIComponent(sessionId)}/suggestions/${encodeURIComponent(id)}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, listid }),
    });

export const dismissSuggestion = (sessionId: string, id: string, userid: string): Promise<{ ok: boolean }> =>
    request(`/suggestions/sessions/${encodeURIComponent(sessionId)}/suggestions/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, status: 'dismissed' }),
    });

// ── Chat sessions ──

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
}

export interface ChatSession {
    id: string;
    userId: string;
    title: string | null;
    month: string | null;
    messages: ChatMessage[];
    messageCount: number;
    createdAt: string;
    updatedAt: string;
}

export const getChatSessions = (userid: string): Promise<{ sessions: ChatSession[] }> =>
    request(`/suggestions/sessions?userid=${encodeURIComponent(userid)}`);

export const getChatSession = (id: string, userid: string): Promise<{ session: ChatSession }> =>
    request(`/suggestions/sessions/${encodeURIComponent(id)}?userid=${encodeURIComponent(userid)}`);

export const deleteChatSession = (id: string): Promise<{ deleted: boolean }> =>
    request(`/suggestions/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });

export interface ChatEvent {
    type: 'session' | 'text' | 'tool_start' | 'tool_result' | 'suggestion' | 'done' | 'error';
    data: any;
}

export const chatWithAssistant = async (
    userid: string,
    message: string,
    history: Array<{ role: string; content: string }>,
    month?: string,
    onEvent?: (event: ChatEvent) => void,
    sessionId?: string,
): Promise<void> => {
    const res = await fetch('/api/suggestions/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid, message, ...(sessionId ? { sessionId } : {}), ...(month ? { month } : {}) }),
    });
    if (!res.ok) throw new Error(`Chat request failed: ${res.status}`);
    if (!res.body) throw new Error('No response body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let eventType = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines, keep incomplete line in buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
            if (line.startsWith('event: ')) {
                eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
                if (eventType) {
                    try {
                        const data = JSON.parse(line.slice(6));
                        if (onEvent) onEvent({ type: eventType as ChatEvent['type'], data });
                    } catch { /* skip malformed JSON */ }
                }
            } else if (line.trim() === '') {
                // Empty line marks end of SSE event — reset for next event
                eventType = '';
            }
        }
    }
};

// ── Helpers ──

export const sugFmt = (n: number | null): string => {
    if (n == null) return '';
    const abs = Math.abs(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (n < 0 ? '\u2212$' : '+$') + abs;
};

export const getConfLabel = (c: number): { label: string; color: string; bg: string } => {
    if (c >= 0.85) return { label: 'High', color: '#fff', bg: '#10B981' };
    if (c >= 0.70) return { label: 'Likely', color: '#fff', bg: '#F59E0B' };
    return { label: 'Maybe', color: '#fff', bg: '#9CA3AF' };
};
